import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { computePercentage } from '../../../helpers/functions';
import { IStrategy, IStrategyAsset } from '../../../types/model';
import { ColorButton, Icon, Table } from '../../UI';
import { TableProps } from '../../UI/Table/Table';
import { ProfitLoss } from '../../ProfitLoss';
import { CustomNumber } from '../../CustomNumber/CustomNumber';
import { Asset } from '../../Asset/Asset';

const transposeArray = (arr: any[]) =>
  arr[0].map((_, colIndex) => arr.map((row) => row[colIndex]));

interface PropTypes {
  onBack: () => void;
  strategy: IStrategy;
  filterType: 'match' | 'unique';
  assetIds: string[];
}

const StrategyTable: React.FC<PropTypes> = ({
  filterType,
  onBack,
  strategy,
  assetIds,
}) => {
  const { t } = useTranslation();

  const data: TableProps['data'] = useMemo(() => {
    if (!assetIds.length || !strategy) {
      return null;
    }

    let assets: IStrategyAsset[] = [];

    if (filterType === 'match') {
      assets = strategy.strategyAssets.items.filter(({ assetId }) =>
        assetIds.includes(assetId)
      );
    } else {
      assets = strategy.strategyAssets.items.filter(
        ({ assetId }) => !assetIds.includes(assetId)
      );
    }

    const tableData = [
      (assets as IStrategyAsset[]).map(
        ({
          connectionType,
          asset: { logo, id, name: description, type, symbol, exchange },
          name,
        }) => (
          <div className="-mx-3">
            <Asset
              type={type || ''}
              logo={logo}
              name={name}
              symbol={symbol}
              id={id}
              connectionType={connectionType}
              exchange={exchange}
              maxChars={8}
            />
          </div>
        )
      ),
      (assets as IStrategyAsset[]).map(({ unit }) => (
        <CustomNumber value={unit} className="w-20" displayType={'text'} />
      )),
      (assets as IStrategyAsset[]).map((item) => {
        const lastPrice =
          item.asset.history[0][strategy.currency.toLowerCase()];

        return (
          <div>
            <ProfitLoss
              percentage={computePercentage(lastPrice, item.entryPrice, 100)}
              value={lastPrice * item.unit}
              displayType={'text'}
            />
          </div>
        );
      }),
    ];
    return transposeArray(tableData);
  }, [assetIds, strategy]);

  return (
    <>
      <div className="flex items-center p-5 border-b gap-2 border-grey-10">
        {/* <span className="material-icons pr-3 cursor-pointer">arrow_back</span> */}
        <Icon svgName="arrowBack" className="cursor-pointer" onClick={onBack} />
        <ColorButton color={strategy.color} isReadOnly={true} radius={3} />
        <div className="font-semibold">
          {t('widget.portfolioOverlap.commonStocksIn', {
            strategy: strategy.name,
          })}
        </div>
      </div>
      <Table
        columns={[
          t('widget.portfolioOverlap.asset').toUpperCase(),
          t('widget.portfolioOverlap.quantity').toUpperCase(),
          t('widget.portfolioOverlap.totalValue').toUpperCase(),
        ]}
        isSortable={true}
        data={data}
      />
    </>
  );
};

export default StrategyTable;
